import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const taskCardVariants = cva(
  "bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg transition-shadow duration-200",
  {
    variants: {
      priority: {
        low: "border-l-4 border-l-green-500",
        medium: "border-l-4 border-l-yellow-500",
        high: "border-l-4 border-l-red-500",
      },
    },
    defaultVariants: {
      priority: "medium",
    },
  }
);

const priorityBadgeVariants = {
  low: "bg-green-100 text-green-700 border-green-200",
  medium: "bg-yellow-100 text-yellow-700 border-yellow-200",
  high: "bg-red-100 text-red-700 border-red-200",
};

export const TaskCard = React.forwardRef(({
  className,
  title,
  description,
  priority = "medium",
  progress = 0,
  maxProgress = 5,
  assignee,
  dueDate,
  icon = "fa-solid fa-list-check",
  ...props
}, ref) => {
  const progressPercentage = (progress / maxProgress) * 100;

  return (
    <div
      ref={ref}
      className={cn(taskCardVariants({ priority }), className)}
      {...props}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <i className={cn(icon, "text-gray-600")} />
          <span className="text-sm font-medium text-gray-900">Task</span>
        </div>
        <span className={cn(
          "px-2 py-1 text-xs font-medium rounded border",
          priorityBadgeVariants[priority]
        )}>
          {priority.charAt(0).toUpperCase() + priority.slice(1)}
        </span>
      </div>

      {/* Content */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">
          {title}
        </h3>
        <p className="text-xs text-gray-600 leading-relaxed">
          {description}
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        {/* Assignee */}
        <div className="flex items-center space-x-2">
          {assignee?.avatar ? (
            <img
              src={assignee.avatar}
              alt={assignee.name}
              className="w-6 h-6 rounded-full"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-xs font-medium text-gray-600">
                {assignee?.name?.charAt(0) || '?'}
              </span>
            </div>
          )}
          <span className="text-xs text-gray-600">
            {dueDate}
          </span>
        </div>

        {/* Progress */}
        <div className="flex items-center space-x-2">
          <span className="text-xs font-medium text-gray-900">
            {progress}/{maxProgress}
          </span>
          <div className="w-12 bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-primary-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

TaskCard.displayName = "TaskCard"; 